// Préchargement.
//
// mpv n'a pas de plafond : il tire l'épisode dans son cache et le relit depuis
// le disque, d'où les 24 minutes en une trentaine de secondes. Dans un
// navigateur, la lecture passe par Media Source Extensions, dont le tampon est
// borné — 60 Mo par défaut dans hls.js, et le navigateur refuse de toute façon
// d'en garder beaucoup plus. Charger l'épisode entier « dans le lecteur » est
// donc hors d'atteinte.
//
// Ce qui est atteignable, c'est de découpler le téléchargement de la lecture :
// on demande les segments à l'avance pour qu'ils atterrissent dans le cache
// HTTP du navigateur — sur disque, dimensionné et purgé par lui — et hls.js les
// relit ensuite sans toucher au réseau. Le proxy les marque cacheables pour ça.

export type Preload = "normal" | "extended" | "full";

export function parsePreload(raw: string | null): Preload {
  return raw === "extended" || raw === "full" ? raw : "normal";
}

/** Connexions simultanées consacrées au préchargement. Le navigateur en ouvre
 *  six par origine en HTTP/1.1 : on en laisse deux à hls.js, sans quoi le
 *  segment dont la lecture a besoin attendrait derrière la file du
 *  préchargement — on gagnerait de l'avance en faisant bégayer l'image. */
const PARALLEL = 4;

export interface WarmProgress {
  done: number;
  total: number;
}

/** Tire une liste de segments dans le cache du navigateur. Les erreurs sont
 *  ignorées : un segment manqué sera simplement redemandé par hls.js le moment
 *  venu, comme si le préchargement n'existait pas. */
export async function warm(
  urls: string[],
  onProgress: (progress: WarmProgress) => void,
  signal: AbortSignal,
): Promise<void> {
  let next = 0;
  let done = 0;

  const worker = async (): Promise<void> => {
    while (next < urls.length && !signal.aborted) {
      const url = urls[next++]!;

      try {
        const res = await fetch(url, { signal });

        // Il faut lire le corps jusqu'au bout : c'est en le lisant que le
        // navigateur l'écrit dans son cache. On le relâche aussitôt — c'est le
        // cache qui garde les octets, pas le tas JavaScript.
        await res.arrayBuffer();
      } catch {
        if (signal.aborted) return;
      }

      done++;
      onProgress({ done, total: urls.length });
    }
  };

  await Promise.all(Array.from({ length: Math.min(PARALLEL, urls.length) }, worker));
}
